import { FaceLandmarker, FilesetResolver } from "@mediapipe/tasks-vision"
import { useEffect, useState } from "react"

import type { FaceMeshCapture } from "./types"

/** Public-path locations of the copied MediaPipe assets (see public/mediapipe). */
export const FACE_MODEL_URL = "/mediapipe/models/face_landmarker.task"
export const FACE_WASM_URL = "/mediapipe/wasm"

let instance: Promise<FaceLandmarker> | null = null

/**
 * Lazily create one shared FaceLandmarker (VIDEO mode, single face) and cache it
 * across every caller — the camera and the processing screen reuse the same
 * instance, and the heavy WASM + model load happens once. A failed load clears
 * the cache so a later caller can retry.
 */
export function loadFaceLandmarker(): Promise<FaceLandmarker> {
  if (!instance) {
    instance = (async () => {
      const resolver = await FilesetResolver.forVisionTasks(FACE_WASM_URL)
      return FaceLandmarker.createFromOptions(resolver, {
        baseOptions: { modelAssetPath: FACE_MODEL_URL, delegate: "GPU" },
        runningMode: "VIDEO",
        numFaces: 1,
      })
    })().catch((err) => {
      instance = null
      throw err
    })
  }
  return instance
}

let imageInstance: Promise<FaceLandmarker> | null = null

/**
 * Lazily create one shared FaceLandmarker in IMAGE mode (single face), separate
 * from the camera's VIDEO instance so detecting a still photo never disturbs the
 * live capture loop. Exported so callers can warm it up ahead of an upload. A
 * failed load clears the cache so a later caller can retry.
 */
export function loadImageFaceLandmarker(): Promise<FaceLandmarker> {
  if (!imageInstance) {
    imageInstance = (async () => {
      const resolver = await FilesetResolver.forVisionTasks(FACE_WASM_URL)
      return FaceLandmarker.createFromOptions(resolver, {
        // CPU (not GPU): this is a single still-image detection, and the camera
        // already holds a GPU graph — a second GPU context can clash on mobile.
        baseOptions: { modelAssetPath: FACE_MODEL_URL, delegate: "CPU" },
        runningMode: "IMAGE",
        numFaces: 1,
      })
    })().catch((err) => {
      imageInstance = null
      throw err
    })
  }
  return imageInstance
}

export interface PreparedUpload {
  /**
   * The uploaded photo with EXIF orientation baked in, as a JPEG data URL. Show
   * THIS (not the raw file) so the displayed pixels are exactly the ones the mesh
   * was detected from — no cross-browser orientation mismatch that would slide the
   * mesh off the face.
   */
  dataUrl: string
  /** Detected face mesh (not mirrored), or null when no face was found. */
  mesh: FaceMeshCapture | null
}

/**
 * Prepare an upload-from-gallery photo: decode it with EXIF orientation applied,
 * re-encode it, and detect the face on those exact pixels. This is the still-image
 * counterpart of the camera's live capture — detection runs once. Phone photos
 * usually carry an EXIF rotation; detecting on the oriented pixels (and showing the
 * same ones) is what makes the mesh sit on the face as tightly as on a selfie.
 *
 * Returns null only when the file can't be decoded at all. A decoded photo with no
 * detectable face still returns its `dataUrl` with `mesh: null`, so the photo shows
 * without an overlay rather than failing the step.
 */
export async function prepareUploadedPhoto(file: Blob): Promise<PreparedUpload | null> {
  let bitmap: ImageBitmap
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" })
  } catch {
    return null
  }
  try {
    // Scale down to roughly the camera's video resolution before drawing. Mesh line
    // widths are in canvas pixels, so a 3–4k-px phone photo would render the mesh
    // hair-thin and wash the thin colored eye/iris layers out entirely once it's
    // scaled into the small oval. ~1080px max side matches the selfie look. The
    // landmarks are normalized, so detecting on the scaled canvas keeps the fit.
    const MAX_SIDE = 1080
    const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height))
    const width = Math.round(bitmap.width * scale)
    const height = Math.round(bitmap.height * scale)
    const canvas = document.createElement("canvas")
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext("2d")
    if (!ctx) return null
    ctx.drawImage(bitmap, 0, 0, width, height)
    const dataUrl = canvas.toDataURL("image/jpeg", 0.92)

    const landmarker = await loadImageFaceLandmarker()
    const results = landmarker.detect(canvas)
    const mesh: FaceMeshCapture | null = results.faceLandmarks?.length
      ? { results, videoWidth: width, videoHeight: height, mirrored: false }
      : null
    return { dataUrl, mesh }
  } finally {
    bitmap.close()
  }
}

export interface FaceLandmarkerState {
  landmarker: FaceLandmarker | null
  loading: boolean
  error: Error | null
}

/**
 * React hook: preload (or reuse) the shared FaceLandmarker. Mount this early
 * (e.g. on the selfie intro) so the model is warm by the time the camera opens.
 */
export function useFaceLandmarker(): FaceLandmarkerState {
  const [state, setState] = useState<FaceLandmarkerState>({
    landmarker: null,
    loading: true,
    error: null,
  })

  useEffect(() => {
    let cancelled = false
    loadFaceLandmarker().then(
      (landmarker) => {
        if (!cancelled) setState({ landmarker, loading: false, error: null })
      },
      (error: unknown) => {
        if (!cancelled)
          setState({ landmarker: null, loading: false, error: error as Error })
      },
    )
    return () => {
      cancelled = true
    }
  }, [])

  return state
}
